import React from 'react';
import styled from 'styled-components';

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  size = 'medium'
}) => {
  const handleToggle = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  return (
    <SwitchContainer
      checked={checked}
      disabled={disabled}
      size={size}
      onClick={handleToggle}
    >
      <SwitchThumb checked={checked} size={size} />
    </SwitchContainer>
  );
};

export default Switch;

// Styled Components
const getSwitchDimensions = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return { width: '32px', height: '18px', thumbSize: '14px' };
    case 'large':
      return { width: '52px', height: '28px', thumbSize: '24px' };
    case 'medium':
    default:
      return { width: '44px', height: '22px', thumbSize: '18px' };
  }
};

const SwitchContainer = styled.div<{
  checked: boolean;
  disabled: boolean;
  size: 'small' | 'medium' | 'large';
}>`
  position: relative;
  width: ${props => getSwitchDimensions(props.size).width};
  height: ${props => getSwitchDimensions(props.size).height};
  background-color: ${props => {
    if (props.disabled) return '#e0e0e0';
    return props.checked ? '#4CAF50' : '#ccc';
  }};
  border-radius: ${props => getSwitchDimensions(props.size).height};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.3s ease;
  display: inline-flex;
  align-items: center;
  
  &:hover {
    background-color: ${props => {
      if (props.disabled) return '#e0e0e0';
      return props.checked ? '#45a049' : '#b3b3b3';
    }};
  }
`;

const SwitchThumb = styled.div<{
  checked: boolean;
  size: 'small' | 'medium' | 'large';
}>`
  position: absolute;
  width: ${props => getSwitchDimensions(props.size).thumbSize};
  height: ${props => getSwitchDimensions(props.size).thumbSize};
  background-color: white;
  border-radius: 50%;
  transition: transform 0.3s ease;
  transform: translateX(${props => {
    const { width, thumbSize } = getSwitchDimensions(props.size);
    const maxTranslate = parseInt(width) - parseInt(thumbSize) - 4;
    return props.checked ? `${maxTranslate}px` : '2px';
  }});
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;
